import { useReactiveVar } from "@apollo/client";
import {
  ApplicationBundleTypeEnum,
  ApplicationMainModelFileTypeEnum,
  ExperimentBundleSubType,
} from "../../../../../generated/graphql";
import { mobileAppModel } from "../../state";
import { getBundleVariant } from "../../utils";
import { BundleNames, PLATFORM_TO_BUNDLE_MAP } from "../../constants";
import { CurrentBundleContainer } from "../../../MobileAppModal.style";
import { Collapse, Typography } from "@material-ui/core";
import { BundleLink } from "../../../../shared/Style/Style";
import { BundleDataType, ExperimentFieldTypes } from "../../types";
import { LocalizationPair } from "./LocalizationPair";

export const AppModelBundles = () => {
  const isLoading = useReactiveVar(mobileAppModel.updateLoading);
  const model = useReactiveVar(mobileAppModel.model);
  const activePlatform = useReactiveVar(mobileAppModel.activePlatform);

  if (!model) return null;

  const { id, bundles, bundleType, modelBundle, contentType } = model;

  const bundlesData = [
    {
      name: BundleNames.Ios,
      type: ExperimentFieldTypes.Bundle,
      fileType: ApplicationMainModelFileTypeEnum.Ios,
      currentModelLink: modelBundle?.iosBundleUrl,
    },
    {
      name: BundleNames.WebGL,
      type: ExperimentFieldTypes.Bundle,
      fileType: ApplicationMainModelFileTypeEnum.WebGl,
      currentModelLink: modelBundle?.webGlBundleUrl,
    },
    {
      name: BundleNames.Android,
      type: ExperimentFieldTypes.Bundle,
      fileType: ApplicationMainModelFileTypeEnum.Android,
      currentModelLink: modelBundle?.androidBundleUrl,
    },
    {
      name: BundleNames.Vr,
      type: ExperimentFieldTypes.Bundle,
      fileType: ApplicationMainModelFileTypeEnum.Vr,
      currentModelLink: modelBundle?.vrBundleUrl,
    },
    {
      name: BundleNames.AndroidBoardBundle,
      type: ExperimentFieldTypes.Bundle,
      fileType: ApplicationMainModelFileTypeEnum.InteractiveWhiteboardAndroid,
      currentModelLink: modelBundle?.interactiveWhiteboardAndroidBundleUrl,
    },
    {
      name: BundleNames.WindowsBoardBundle,
      fileType: ApplicationMainModelFileTypeEnum.InteractiveWhiteboardWindows,
      currentModelLink: modelBundle?.interactiveWhiteboardWindowsBundleUrl,
      type: ExperimentFieldTypes.Bundle,
    },
    {
      name: BundleNames.IosManifest,
      type: ExperimentFieldTypes.Manifest,
      fileType: ApplicationMainModelFileTypeEnum.Ios,
      currentModelLink: modelBundle?.iosManifestUrl,
    },
    {
      name: BundleNames.WebGlManifest,
      type: ExperimentFieldTypes.Manifest,
      fileType: ApplicationMainModelFileTypeEnum.WebGl,
      currentModelLink: modelBundle?.webGlManifestUrl,
    },
    {
      name: BundleNames.AndroidManifest,
      type: ExperimentFieldTypes.Manifest,
      fileType: ApplicationMainModelFileTypeEnum.Android,
      currentModelLink: modelBundle?.androidManifestUrl,
    },
    {
      name: BundleNames.VrManifest,
      type: ExperimentFieldTypes.Manifest,
      fileType: ApplicationMainModelFileTypeEnum.Vr,
      currentModelLink: modelBundle?.vrManifestUrl,
    },
    {
      name: BundleNames.AndroidBoardManifest,
      type: ExperimentFieldTypes.Manifest,
      fileType: ApplicationMainModelFileTypeEnum.InteractiveWhiteboardAndroid,
      currentModelLink: modelBundle?.interactiveWhiteboardAndroidManifestUrl,
    },
    {
      name: BundleNames.WindowsBoardManifest,
      type: ExperimentFieldTypes.Manifest,
      fileType: ApplicationMainModelFileTypeEnum.InteractiveWhiteboardWindows,
      currentModelLink:
        model.modelBundle?.interactiveWhiteboardWindowsManifestUrl,
    },
    {
      name: BundleNames.IosResources,
      type: ExperimentBundleSubType.Resources,
      fileType: ApplicationMainModelFileTypeEnum.Ios,
    },
    {
      name: BundleNames.IosResourcesManifest,
      type: ExperimentBundleSubType.ResourcesManifest,
      fileType: ApplicationMainModelFileTypeEnum.Ios,
    },
    {
      name: BundleNames.AndroidResources,
      type: ExperimentBundleSubType.Resources,
      fileType: ApplicationMainModelFileTypeEnum.Android,
    },
    {
      name: BundleNames.AndroidResourcesManifest,
      type: ExperimentBundleSubType.ResourcesManifest,
      fileType: ApplicationMainModelFileTypeEnum.Android,
    },
    {
      name: BundleNames.WebGlResources,
      type: ExperimentBundleSubType.Resources,
      fileType: ApplicationMainModelFileTypeEnum.WebGl,
    },
    {
      name: BundleNames.WebGlResourceManifests,
      type: ExperimentBundleSubType.ResourcesManifest,
      fileType: ApplicationMainModelFileTypeEnum.WebGl,
    },
    {
      name: BundleNames.VrResources,
      type: ExperimentBundleSubType.Resources,
      fileType: ApplicationMainModelFileTypeEnum.Vr,
    },
    {
      name: BundleNames.VrResourcesManifest,
      type: ExperimentBundleSubType.ResourcesManifest,
      fileType: ApplicationMainModelFileTypeEnum.Vr,
    },
    {
      name: BundleNames.AndroidBoardResources,
      type: ExperimentBundleSubType.Resources,
      fileType: ApplicationMainModelFileTypeEnum.InteractiveWhiteboardAndroid,
    },
    {
      name: BundleNames.WindowsBoardResources,
      type: ExperimentBundleSubType.Resources,
      fileType: ApplicationMainModelFileTypeEnum.InteractiveWhiteboardWindows,
    },
    {
      name: BundleNames.AndroidBoardResourcesManifest,
      type: ExperimentBundleSubType.ResourcesManifest,
      fileType: ApplicationMainModelFileTypeEnum.InteractiveWhiteboardAndroid,
    },
    {
      name: BundleNames.WindowsBoardResourcesManifest,
      type: ExperimentBundleSubType.ResourcesManifest,
      fileType: ApplicationMainModelFileTypeEnum.InteractiveWhiteboardWindows,
    },
  ];

  const visibleBundles = PLATFORM_TO_BUNDLE_MAP[activePlatform] || [];

  const filteredBundlesData = bundlesData.filter((bundle) =>
    visibleBundles.includes(bundle.name),
  );

  const isLocalization = ({ type }: BundleDataType) =>
    type === ExperimentBundleSubType.Resources ||
    type === ExperimentBundleSubType.ResourcesManifest;

  // Bundle and manifest come from the ModelBundle and are read-only here. Localization is the only
  // pair the admin uploads, and it is rendered as one unit by LocalizationPair.
  const readOnlyBundles = filteredBundlesData.filter(
    (bundle) => !isLocalization(bundle),
  );
  const resourcesLabel = filteredBundlesData.find(
    ({ type }) => type === ExperimentBundleSubType.Resources,
  )?.name;
  const resourcesManifestLabel = filteredBundlesData.find(
    ({ type }) => type === ExperimentBundleSubType.ResourcesManifest,
  )?.name;

  const activeBundle = bundles.find((bundle) => bundle.type === activePlatform);

  const renderCurrentBundle = ({
    name,
    currentModelLink,
    type,
    fileType,
  }: BundleDataType) => {
    const currentBundle = bundles.find((bundle) => bundle.type === fileType);
    const bundleLink =
      getBundleVariant(currentBundle, type) || currentModelLink;

    if (!bundleLink) return null;

    return (
      <>
        {!currentBundle?.id && type === ExperimentFieldTypes.Bundle && (
          <Typography
            variant="body2"
            color="error"
            style={{ maxWidth: "480px", marginTop: "16px" }}
          >
            Оновлено модель бандлу. Щоб завантажити файли локалізації, спочатку
            збережіть оновлену модель, натиснувши кнопку "Зберегти"
          </Typography>
        )}
        <CurrentBundleContainer key={name}>
          <BundleLink href={bundleLink}>{name}</BundleLink>
        </CurrentBundleContainer>
      </>
    );
  };

  return (
    <>
      <Collapse
        in={
          bundleType === ApplicationBundleTypeEnum.Bundle && !!contentType?.name
        }
        collapsedSize="0px"
      >
        {readOnlyBundles.map((data) => renderCurrentBundle({ ...data }))}

        {!!resourcesLabel && !!resourcesManifestLabel && (
          <LocalizationPair
            // Remount per model and platform: picked-but-unsaved files belong to the row they
            // were chosen for, and must never follow the admin to another bundle row. The modal
            // can be re-pointed at a different model without unmounting, so the id is part of
            // the identity too.
            key={`${id}-${activePlatform}`}
            modelId={id}
            currentBundle={activeBundle}
            resourcesLabel={resourcesLabel}
            resourcesManifestLabel={resourcesManifestLabel}
            isLoading={isLoading}
          />
        )}
      </Collapse>
    </>
  );
};
